import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import AdBanner from "@/components/AdBanner";
import ThemeToggle from "@/components/ThemeToggle";
import SoundToggle from "@/components/SoundToggle";
import { PageLoader } from "@/components/Kit";
import SettingsModal from "@/components/SettingsModal";
import Onboarding from "@/components/Onboarding";
import { toast } from "sonner";
import { Zap, Thermometer, Coins, Sparkles, LogOut, Settings as SettingsIcon, Bell, Menu, X, ChevronDown,
  MessageSquare, Store, Search, Bot, ArrowLeftRight, Compass, Ticket, Gamepad2, Swords, Dumbbell, Wrench,
  Gift, Landmark, Package, Users, Gavel, ShoppingCart } from "lucide-react";

// 2013-style themed quick links (left rail top section)
const CLASSIC = [
  ["The Comms Deck", "/app/mail", MessageSquare],
  ["User Scrap-Shops", "SHOP", Store],
  ["The Net-Crawler", "/app/shops/wizard", Search],
  ["Robot Lookups", "/app/fleet/status", Bot],
  ["Trading Post", "/app/trade", ArrowLeftRight],
  ["CoreQuest", "/app/corequest", Compass],
  ["Scrap Tombola", "/app/arcade/wheel", Ticket],
];
// full station directory (keeps every route reachable after the chrome swap)
const ROOMS = [
  ["/app/station", "Station Map", Compass], ["/app/arcade", "Central Core", Gamepad2],
  ["/app/arcade/wheel", "Wheel of Overclocking", Ticket], ["/app/advent", "Advent Feed", Gift],
  ["/app/arena", "Battle Arena", Swords], ["/app/training", "Training Bay", Dumbbell],
  ["/app/shop", "Cyber Bazaar", ShoppingCart], ["/app/market", "Market", Coins],
  ["/app/auction", "Auction House", Gavel], ["/app/trade", "Trading Post", ArrowLeftRight],
  ["/app/bank", "Core Reserve Bank", Landmark], ["/app/vault", "My Vault", Package],
  ["/app/storage/sdb", "Safety Deposit Box", Package], ["/app/loops", "Salvage Loops", Wrench],
  ["/app/storage/fusion", "Fusion Burner", Sparkles], ["/app/shops/wizard", "Net-Crawler", Search],
  ["/app/discard-pile", "Discard Pile", Package], ["/app/corequest", "CoreQuest", Compass],
  ["/app/fleet/status", "Fleet Status", Users], ["/app/mainframe-news", "Mainframe News", MessageSquare],
  ["/app/quests", "Quest Terminal", Compass], ["/app/scrapyard", "Scrap Yard", Wrench],
  ["/app/charging", "Charging Dock", Zap], ["/app/pets", "My Robots", Users],
  ["/app/depot", "Fabrication Depot", Wrench], ["/app/friends", "Friends", Users],
];

function Wallet({ user }) {
  return (
    <div className="flex items-center gap-3">
      <div data-testid="wallet-bits" className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary/15 border border-secondary/30">
        <Coins className="w-4 h-4 text-secondary" />
        <span className="mono text-sm font-bold text-secondary">{(user.bits ?? user.credits ?? 0).toLocaleString()}</span>
        <span className="label-cap text-[8px]">Bits</span>
      </div>
      <div data-testid="wallet-cosmium" className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#5cc8ff]/10 border border-[#5cc8ff]/40">
        <Sparkles className="w-4 h-4 text-[#5cc8ff]" />
        <span className="mono text-sm font-bold text-[#5cc8ff]">{(user.cosmium ?? 0).toLocaleString()}</span>
        <span className="label-cap text-[8px] text-[#5cc8ff]/80">Cosmium</span>
      </div>
    </div>
  );
}

function EventMarquee({ ev, onClose }) {
  if (!ev) return null;
  const bad = ev.kind === "bad";
  return (
    <div data-testid="random-event-marquee"
      className={`absolute top-0 left-0 right-0 z-40 px-4 py-2 flex items-center gap-3 border-b ${bad ? "bg-red-600/90 border-red-300" : "bg-emerald-600/90 border-emerald-300"} text-white shadow-lg pop-in`}>
      <Sparkles className="w-4 h-4 shrink-0" />
      <span className="text-sm font-semibold flex-1 truncate">{ev.message}</span>
      <button data-testid="event-marquee-close" onClick={onClose} className="shrink-0 opacity-80 hover:opacity-100"><X className="w-4 h-4" /></button>
    </div>
  );
}

export default function StationLayout() {
  const { user, loading, logout, patchUser, refresh } = useAuth();
  const [pet, setPet] = useState(undefined);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [event, setEvent] = useState(null);
  const [showRooms, setShowRooms] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const lastPath = useRef("");

  const refreshPet = useCallback(async () => {
    try { const { data } = await api.get("/pet"); setPet(data.pet); if (data.pet) localStorage.setItem("activePetType", data.pet.cpu); return data.pet; }
    catch { setPet(null); return null; }
  }, []);

  useEffect(() => { if (user) refreshPet(); }, [user, refreshPet]);
  useEffect(() => { setMobileNav(false); }, [location.pathname]);

  // Random Events engine: 2% chance on internal route change → apply + marquee
  useEffect(() => {
    if (!user || !pet) return;
    if (lastPath.current === location.pathname) return;
    const first = lastPath.current === "";
    lastPath.current = location.pathname;
    if (first) return;
    if (Math.random() >= 0.02) return;
    (async () => {
      try {
        const { data } = await api.post("/events/roll");
        setEvent(data); patchUser({ bits: data.wallet.bits, credits: data.wallet.bits, cosmium: data.wallet.cosmium });
        setTimeout(() => setEvent(null), 7000);
      } catch { /* ignore */ }
    })();
  }, [location.pathname, user, pet, patchUser]);

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/app/login" replace />;
  if (pet === undefined) return <PageLoader label="Booting station terminal" />;
  if (!pet && !location.pathname.includes("/create")) return <Navigate to="/app/create" replace />;

  const linkClass = ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-[var(--radius)] text-sm font-semibold transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`;

  const sidebar = (onClick) => (
    <>
      <div className="label-cap px-2 pt-1 pb-1.5 text-primary">Station Directory</div>
      {CLASSIC.map(([label, to, Icon]) => {
        const href = to === "SHOP" ? `/app/shops/user/${user.user_id}` : to;
        return (
          <NavLink key={label} to={href} onClick={onClick} data-testid={`classic-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={linkClass}>
            <Icon className="w-4 h-4" /> <span className="truncate">{label}</span>
          </NavLink>
        );
      })}
      <button onClick={() => setShowRooms((s) => !s)} className="flex items-center justify-between px-3 py-2 mt-2 label-cap hover:text-primary">
        All Rooms <ChevronDown className={`w-3 h-3 transition-transform ${showRooms ? "" : "-rotate-90"}`} />
      </button>
      {showRooms && ROOMS.map(([to, label, Icon]) => (
        <NavLink key={to + label} to={to} onClick={onClick} end className={linkClass}>
          <Icon className="w-4 h-4" /> <span className="truncate">{label}</span>
        </NavLink>
      ))}
      {pet && (
        <div className="mt-auto panel p-2 space-y-1.5">
          <div className="label-cap text-[9px]">Telemetry</div>
          <div className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-primary" /><div className="flex-1 h-1.5 rounded bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${pet.battery}%` }} /></div></div>
          <div className="flex items-center gap-1.5"><Thermometer className="w-3 h-3 text-secondary" /><div className="flex-1 h-1.5 rounded bg-muted overflow-hidden"><div className="h-full bg-secondary" style={{ width: `${pet.thermal}%` }} /></div></div>
        </div>
      )}
    </>
  );

  return (
    <div className="h-screen w-full overflow-hidden grid-bg flex flex-col">
      <div className="shrink-0 pt-1.5 pb-1" style={{ paddingLeft: "0.25in", paddingRight: "0.25in" }}>
        <AdBanner height={94} />
      </div>

      {/* 2013-style header banner */}
      <header className="shrink-0 border-b-2 border-primary/40 bg-card/80 backdrop-blur flex items-center justify-between px-2 sm:px-4 gap-3 py-2">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="mobile-nav-toggle" onClick={() => setMobileNav(true)} className="md:hidden text-muted-foreground hover:text-primary p-1"><Menu className="w-5 h-5" /></button>
          {pet ? (
            <button data-testid="header-active-robot" onClick={() => navigate("/app/pets")} className="flex items-center gap-2 pr-3 border-r border-border shrink-0 hover:opacity-90">
              <div className="rounded-lg overflow-hidden border border-primary/40 bg-background/50">
                <LayeredStack layers={pet.layers} core={pet.core_color} rig={pet.rig} cpu={pet.cpu} size={48} state={pet.battery <= 0 ? "off" : "idle"} />
              </div>
              <div className="min-w-0 text-left">
                <div className="font-head font-bold text-xs truncate leading-tight">{pet.name}</div>
                <div className="label-cap text-[9px]">SCL-{user.scl} · Change ▸</div>
              </div>
            </button>
          ) : <span className="label-cap pr-3 border-r border-border">NO UNIT</span>}
          <span className="font-head font-black text-primary neon-text text-base shrink-0">ORBIT</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <Wallet user={user} />
          <button data-testid="settings-btn" onClick={() => setSettingsOpen(true)} className="text-muted-foreground hover:text-primary p-1"><SettingsIcon className="w-4 h-4" /></button>
          <div className="hidden sm:flex items-center gap-3"><ThemeToggle /><SoundToggle /></div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/app/login"); }} className="text-muted-foreground hover:text-accent"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        <aside className="hidden md:flex w-[clamp(210px,20vw,280px)] shrink-0 border-r border-border bg-card/50 backdrop-blur p-2 flex-col gap-0.5 overflow-y-auto no-scrollbar">
          {sidebar()}
        </aside>

        {mobileNav && (
          <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-nav-drawer">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileNav(false)} />
            <div className="absolute inset-y-0 left-0 w-[82vw] max-w-[300px] bg-card border-r border-border p-2 flex flex-col gap-0.5 overflow-y-auto pop-in">
              <div className="flex items-center justify-between px-2 py-1 mb-1">
                <span className="font-head font-black text-primary neon-text text-sm">ORBIT</span>
                <button onClick={() => setMobileNav(false)} className="text-muted-foreground hover:text-accent p-1"><X className="w-5 h-5" /></button>
              </div>
              {sidebar(() => setMobileNav(false))}
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 overflow-y-auto relative">
          <EventMarquee ev={event} onClose={() => setEvent(null)} />
          <div className="p-4 sm:p-8">
            <div className="max-w-5xl mx-auto pop-in">
              <Outlet context={{ pet, refreshPet, user, patchUser, refreshUser: refresh, openSettings: () => setSettingsOpen(true) }} />
            </div>
          </div>
        </main>
      </div>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} user={user} refreshUser={refresh} />
      {!user.onboarded && <Onboarding onDone={refresh} />}
    </div>
  );
}
